import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    PANEL_PRIORITIES,
    STATUS_LIST_PANEL_PREVIEW_IMAGE,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { useQueryLoader } from '../../data-table/Loaders';
import { filterId as hypervisorFilterId } from '../../filters/hypervisor-filter/HypervisorFilter';
import { filterId as virtualMachineFilterId } from '../../filters/virtual-machine-filter/VirtualMachineFilter';
import ReportingDataTable from '../../data-table/ReportingDataTable';
import { metricDefFormatter } from '../../../utilities';
import { listTooltip, percentConfig, percentSparkLinkFn } from './funcs/IpmList';
import { businessServiceFilterId, statusFilterId, timeRangeFilterId } from '../../filters';

const title = t`Virtual Machines`;

const dataSource = {
    url: '/query/table',
    params: {
        type: 'server',
        subType: 'virtualmachine',
        entityFields: ['ipAddress', 'name', 'dataCenter', 'tags', 'cluster', 'resourcePool', 'description'],
        host: true,
        metrics: ['status', 'avgCpu', 'avgMem', 'vmDiskMaxLatency'],
        trends: ['avgCpu', 'avgMem', 'status'],
        renderAsEntity: true,
    },
};

const CPU = t`CPU`;
const MEMORY = t`Memory`;

const columns = [
    {
        type: COLUMN_TYPES.DRILLINLINK,
        title: t`Name`,
        sortable: true,
        mapping: 'server.name',
        getTooltip: listTooltip('server.name'),
        width: 150,
        link: {
            target: '../vmware-vm-detail',
            mappedFilterParams: {
                [virtualMachineFilterId]: 'server',
            },
        },
    },
    {
        type: COLUMN_TYPES.DRILLINLINK,
        title: t`IP Address`,
        sortable: true,
        mapping: 'server.ipAddress',
        getTooltip: listTooltip('server.ipAddress'),
        width: 130,
        link: {
            target: '../vmware-vm-detail',
            mappedFilterParams: {
                [virtualMachineFilterId]: 'server',
            },
        },
    },
    {
        type: COLUMN_TYPES.DRILLINLINK,
        title: t`Hypervisor`,
        sortable: true,
        mapping: 'server.virtualization.hyperVisorName',
        getTooltip: listTooltip('server.virtualization.hyperVisorName'),
        width: 130,
        link: {
            target: '../vmware-host-detail',
            mappedFilterParams: {
                [hypervisorFilterId]: (row) => ({ uuid: row.server.virtualization.parentUuid }),
            },
        },
    },
    {
        type: COLUMN_TYPES.NUMERIC,
        title: '',
        assocId: CPU, // associated with CPU column
        csvTitle: CPU,
        sortable: false,
        mapping: 'avgCpu',
        width: 60,
        formatter: metricDefFormatter(percentConfig),
    },
    {
        type: COLUMN_TYPES.SPARKLINE,
        title: CPU,
        sortable: true,
        sortProperty: 'avgCpu',
        width: 120,
        mapper: (rowData) => percentSparkLinkFn('avgCpu', 'CPU Usage')(rowData),
    },
    {
        type: COLUMN_TYPES.NUMERIC,
        title: '',
        assocId: MEMORY, // associated with Memory column
        csvTitle: MEMORY,
        sortable: false,
        mapping: 'avgMem',
        width: 60,
        formatter: metricDefFormatter(percentConfig),
    },
    {
        type: COLUMN_TYPES.SPARKLINE,
        title: MEMORY,
        sortable: true,
        sortProperty: 'avgMem',
        width: 120,
        mapper: (rowData) => percentSparkLinkFn('avgMem', 'Mem Usage')(rowData),
    },
    {
        type: COLUMN_TYPES.NUMERIC,
        title: t`Peak Disk Latency`,
        sortable: true,
        mapping: 'vmDiskMaxLatency',
        width: 160,
        formatter: metricDefFormatter({ units: ' ms', multiplier: 0 }),
    },
    {
        type: COLUMN_TYPES.STATUSTREND,
        title: t`Status Trend`,
        sortable: true,
        sortProperty: 'status',
        mapping: 'trends.status.data',
    },
];

const sorters = [{ property: 'status', direction: 'desc' }];

function VirtualMachineListPanel(props) {
    const loader = useQueryLoader(dataSource);
    return (
        <ReportingDataTable
            panelProps={props}
            loader={loader}
            columns={columns}
            sorters={sorters}
            deletedMapping="server.deleted"
        />
    );
}

VirtualMachineListPanel.propTypes = panelPropTypes;

VirtualMachineListPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    autoHeight: true,
    minContentHeight: 130,
};

export default Panel({
    typeAlias: 'VmwareVmList',
    catalogEntry: {
        category: PANEL_CATEGORIES.VIRTUAL_MACHINE,
        priority: PANEL_PRIORITIES.LIST,
        title,
        description: t`List of virtual machines (VMs), showing status trends`,
        previewImage: STATUS_LIST_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId],
        optional: [businessServiceFilterId, hypervisorFilterId, statusFilterId],
    },
})(VirtualMachineListPanel);
